import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const toDate = (iso) => {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const [y, m, d] = iso.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  return Number.isNaN(date.getTime()) ? null : date;
};

const toIso = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
const normalizeRangeType = (value) => (String(value || "").toLowerCase() === "after" ? "after" : "between");

const monthLabel = (date) =>
  date.toLocaleString("en-US", {
    month: "long",
    year: "numeric",
  });

function MonthView({
  monthDate,
  selectedStart,
  selectedEnd,
  onSelectDate,
  isDisabledDate,
  today,
  rangeType = "between",
  showTitle = false,
}) {
  const y = monthDate.getFullYear();
  const m = monthDate.getMonth();
  const first = new Date(y, m, 1);
  const startWeekday = first.getDay();
  const daysInMonth = new Date(y, m + 1, 0).getDate();
  const prevDays = new Date(y, m, 0).getDate();

  const cells = [];
  for (let i = 0; i < startWeekday; i += 1) {
    const day = prevDays - startWeekday + i + 1;
    cells.push({ day, inMonth: false, date: new Date(y, m - 1, day) });
  }
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push({ day, inMonth: true, date: new Date(y, m, day) });
  }
  while (cells.length % 7 !== 0) {
    const day = cells.length - (startWeekday + daysInMonth) + 1;
    cells.push({ day, inMonth: false, date: new Date(y, m + 1, day) });
  }

  const isInRange = (date) => {
    if (rangeType === "after") return false;
    if (!selectedStart || !selectedEnd) return false;
    const d = startOfDay(date).getTime();
    return d >= startOfDay(selectedStart).getTime() && d <= startOfDay(selectedEnd).getTime();
  };

  return (
    <div className="w-full">
      {showTitle && <div className="text-center text-md font-semibold mb-2">{monthLabel(monthDate)}</div>}
      <div className="grid grid-cols-7 text-sm text-gray-500 mb-1">
        {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
          <div key={d} className="text-center py-1.5">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {cells.map((cell, idx) => {
          const disabled = isDisabledDate(cell.date);
          const isStart = selectedStart && toIso(cell.date) === toIso(selectedStart);
          const isEnd =
            rangeType === "between" && selectedEnd && toIso(cell.date) === toIso(selectedEnd);
          const inRange = isInRange(cell.date);
          const isToday = rangeType === "between" && today && toIso(cell.date) === toIso(today);
          const roundedClass = isStart
            ? "rounded-l-xl"
            : isEnd
            ? "rounded-r-xl"
            : "";
          const singleDayRange =
            isStart &&
            isEnd &&
            selectedStart &&
            selectedEnd &&
            toIso(selectedStart) === toIso(selectedEnd);

          return (
            <button
              key={`${idx}-${toIso(cell.date)}`}
              type="button"
              disabled={disabled}
              onClick={() => onSelectDate(cell.date)}
              className={`h-8 text-sm flex items-center justify-center transition-colors ${
                !cell.inMonth ? "text-gray-300" : "text-gray-800"
              } ${disabled ? "cursor-not-allowed opacity-40" : "hover:bg-gray-100"} ${
                inRange ? `bg-indigo-100 ${roundedClass}` : ""
              } ${isToday && !isStart && !isEnd ? "bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200 rounded-xl" : ""} ${
                disabled ? "text-gray-300" : ""
              } ${
                singleDayRange
                  ? "bg-indigo-600 text-white rounded-xl hover:bg-indigo-600"
                  : rangeType === "after" && isStart
                  ? "bg-indigo-600 text-white rounded-xl hover:bg-indigo-600 ring-2 ring-indigo-200 ring-inset"
                  : isStart || isEnd
                  ? "bg-indigo-600 text-white rounded-xl hover:bg-indigo-600 ring-2 ring-indigo-200 ring-inset"
                  : ""
              }`}
            >
              {cell.day}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function ModernDateRangePicker({
  startDate = "",
  endDate = "",
  onChange,
  maxRangeDays = 90,
  placeholder = "Select Date Range",
  fullWidth = false,
  rangeType = "between",
  allowAfter = true,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const today = startOfDay(new Date());
  const parsedStart = toDate(startDate);
  const parsedEnd = toDate(endDate);
  const normalizedRangeType = normalizeRangeType(rangeType);
  const initialMonth = parsedStart || new Date();
  const [leftMonth, setLeftMonth] = useState(new Date(initialMonth.getFullYear(), initialMonth.getMonth(), 1));
  const [draftStart, setDraftStart] = useState(parsedStart);
  const [draftEnd, setDraftEnd] = useState(parsedEnd);
  const [draftRangeType, setDraftRangeType] = useState(normalizedRangeType);

  const rightMonth = useMemo(
    () => new Date(leftMonth.getFullYear(), leftMonth.getMonth() + 1, 1),
    [leftMonth]
  );

  const displayText =
    draftRangeType === "after"
      ? draftStart
        ? `${toIso(draftStart)} onwards`
        : ""
      : draftStart && draftEnd
      ? `${toIso(draftStart)} to ${toIso(draftEnd)}`
      : "";

  const resetDraftFromProps = () => {
    const s = toDate(startDate);
    const e = toDate(endDate);
    const nextRangeType = normalizedRangeType;
    setDraftStart(s);
    setDraftEnd(nextRangeType === "after" ? null : e);
    setDraftRangeType(nextRangeType);
    const base = s || new Date();
    setLeftMonth(new Date(base.getFullYear(), base.getMonth(), 1));
  };

  useEffect(() => {
    if (isOpen) return;
    const s = toDate(startDate);
    const e = toDate(endDate);
    const nextRangeType = normalizedRangeType;
    setDraftStart(s);
    setDraftEnd(nextRangeType === "after" ? null : e);
    setDraftRangeType(nextRangeType);
  }, [startDate, endDate, normalizedRangeType, isOpen]);

  const handleSelectDate = (date) => {
    const d = startOfDay(date);
    if (draftRangeType === "after") {
      setDraftStart(d);
      setDraftEnd(null);
      return;
    }
    if (!draftStart || (draftStart && draftEnd)) {
      setDraftStart(d);
      setDraftEnd(null);
      return;
    }
    if (d.getTime() < draftStart.getTime()) {
      setDraftStart(d);
      return;
    }
    const diffDays = Math.floor((d.getTime() - draftStart.getTime()) / (24 * 60 * 60 * 1000));
    if (diffDays > maxRangeDays) {
      setDraftEnd(new Date(draftStart.getFullYear(), draftStart.getMonth(), draftStart.getDate() + maxRangeDays));
      return;
    }
    setDraftEnd(d);
  };

  const isDisabledDate = (date) => {
    const d = startOfDay(date).getTime();
    const t = today.getTime();
    if (d > t) return true;
    if (draftRangeType === "after") return false;
    if (!draftStart || draftEnd) return false;
    const s = startOfDay(draftStart).getTime();
    const rangeMax = s + maxRangeDays * 24 * 60 * 60 * 1000;
    return d < s || d > Math.min(rangeMax, t);
  };

  const applySelection = () => {
    if (draftRangeType === "after") {
      if (!draftStart) {
        onChange({ startDate: "", endDate: "", rangeType: "after" });
        setIsOpen(false);
        return;
      }
      onChange({ startDate: toIso(draftStart), endDate: "", rangeType: "after" });
      setIsOpen(false);
      return;
    }
    if (!draftStart || !draftEnd) {
      onChange({ startDate: "", endDate: "", rangeType: "between" });
      setIsOpen(false);
      return;
    }
    onChange({ startDate: toIso(draftStart), endDate: toIso(draftEnd), rangeType: "between" });
    setIsOpen(false);
  };

  const selectionSummary =
    draftRangeType === "after"
      ? draftStart
        ? `After Date: ${toIso(draftStart)}`
        : "After Date: -"
      : draftStart && draftEnd
      ? `Start: ${toIso(draftStart)}  End: ${toIso(draftEnd)}`
      : "Start: -  End: -";

  return (
    <div className={`relative ${fullWidth ? "w-full" : "inline-block"}`}>
      <button
        type="button"
        className={`${fullWidth ? "w-full" : "w-[18rem] max-w-full"} px-3 py-2 text-left border border-gray-200 rounded-md bg-white text-sm`}
        onClick={() => {
          if (!isOpen) resetDraftFromProps();
          setIsOpen((v) => !v);
        }}
      >
        {displayText || placeholder}
      </button>
      {isOpen && (
        <div className="absolute left-0 z-[999999] mt-2 bg-white border border-gray-200 rounded-xl shadow-xl p-2 w-[30rem] max-w-[min(46rem,calc(100vw-2rem))]">
          <div className="mb-2 flex items-center gap-2 border-b border-gray-200 pb-2">
            <button
              type="button"
              className={`px-2.5 py-1 text-xs rounded-md border ${
                draftRangeType === "between"
                  ? "bg-indigo-600 text-white border-indigo-600"
                  : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
              }`}
              onClick={() => {
                setDraftRangeType("between");
                if (draftStart && !draftEnd) {
                  setDraftEnd(null);
                }
              }}
            >
              Between
            </button>
            {allowAfter ? (
              <button
                type="button"
                className={`px-2.5 py-1 text-xs rounded-md border ${
                  draftRangeType === "after"
                    ? "bg-indigo-600 text-white border-indigo-600"
                    : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                }`}
                onClick={() => {
                  setDraftRangeType("after");
                  if (draftStart) setDraftEnd(null);
                }}
              >
                After
              </button>
            ) : null}
          </div>
          <div className="grid grid-cols-[auto_1fr_1fr_auto] items-center gap-2 mb-1">
            <button
              type="button"
              className="p-1 rounded-md hover:bg-gray-100"
              onClick={() => setLeftMonth(new Date(leftMonth.getFullYear(), leftMonth.getMonth() - 1, 1))}
            >
              <ChevronLeft size={16} />
            </button>
            <div className="text-center text-md font-semibold">{monthLabel(leftMonth)}</div>
            <div className="text-center text-md font-semibold">{monthLabel(rightMonth)}</div>
            <button
              type="button"
              className="p-1 rounded-md hover:bg-gray-100"
              onClick={() => setLeftMonth(new Date(leftMonth.getFullYear(), leftMonth.getMonth() + 1, 1))}
            >
              <ChevronRight size={16} />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <MonthView
              monthDate={leftMonth}
              selectedStart={draftStart}
              selectedEnd={draftEnd}
              rangeType={draftRangeType}
              onSelectDate={handleSelectDate}
              isDisabledDate={isDisabledDate}
              today={today}
              showTitle={false}
            />
            <MonthView
              monthDate={rightMonth}
              selectedStart={draftStart}
              selectedEnd={draftEnd}
              rangeType={draftRangeType}
              onSelectDate={handleSelectDate}
              isDisabledDate={isDisabledDate}
              today={today}
              showTitle={false}
            />
          </div>
          <div className="pt-3 mt-3 border-t border-gray-200 flex items-center justify-between gap-2">
            <div className="text-xs text-gray-600">{selectionSummary}</div>
            <div className="flex justify-end gap-2">
            <button
              type="button"
              className="px-3 py-1.5 text-xs border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              onClick={() => {
                resetDraftFromProps();
                setIsOpen(false);
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="px-3 py-1.5 text-xs bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-md"
              onClick={applySelection}
            >
              Apply
            </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
